import { Avatar, Badge, Button, Card, CardHeader, Chip, Drawer, EmptyState, Icon, SegmentedTabs, Skeleton, StrikeText, Tick, ViewAll, formatCount, formatDate, formatMoney, formatTime, useToast, type ChipKind } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { createTask, doneFollowup, doneTask, reopenTask, snoozeFollowup, updateAppointment, type AppointmentAction, type AppointmentItem, type FollowupItem, type TaskItem } from "../../api/home";
import { useServices } from "../../app/services";
import { addDays, minutesOfDay, today as localDate } from "../../lib/dates";
import { isUnmasked, telHref, whatsappHref } from "../../lib/phone";
import { useAppointments, useFollowups, useTasks, type Scope } from "./hooks";
import { FailureBody } from "./states";
import { home } from "./strings";
import s from "./home.module.css";

const t = home;

/* ------------------------------------------------------------------ shared bits */

function SkeletonRows({ n = 4 }: { n?: number }) {
  return (
    <div className={s.wb} aria-busy="true">
      {Array.from({ length: n }, (_, i) => (
        <div className={s.skRow} key={i}><Skeleton width={18} height={18} /><div><Skeleton width="60%" height={13} /><Skeleton width="85%" height={12} /></div></div>
      ))}
    </div>
  );
}

const chipKind = (kind: string): ChipKind => (kind === "renewal" ? "renewal" : kind === "due" ? "dues" : kind === "inactive" ? "inactive" : kind === "lead_trial" ? "trial" : "enquiry");

function dueLabel(f: FollowupItem, sc: Scope): string {
  const { locale, timeZone } = sc.ctx;
  const day = f.dueDate ?? (f.dueAt !== null ? localDate(f.dueAt, timeZone) : null);
  if (!day) return "";
  if (day === sc.date) return f.dueAt !== null ? formatTime(f.dueAt, timeZone) : formatDate(day, locale, "weekday");
  return day === addDays(sc.date, -1) ? "Yesterday" : formatDate(day, locale, "weekday");
}

/** The work list: overdue or due by `date`. The feed also carries the coming week's renewals, for the Renewals card. */
const dueNow = (items: readonly FollowupItem[], date: string) => items.filter((f) => f.overdue || f.dueDate === null || f.dueDate <= date);

function detailText(f: FollowupItem, sc: Scope): string {
  const kind = t.followups.kinds[f.kind] ?? "Follow-up";
  const bits: string[] = [kind];
  if (f.kind === "due" && f.amountDue !== undefined && sc.ctx.me) bits.push(`${formatMoney(f.amountDue, sc.ctx.me.business.currency, sc.ctx.locale)} due`);
  if (f.dueDate && f.kind === "renewal") bits.push(`plan ends ${formatDate(f.dueDate, sc.ctx.locale, "weekday")}`);
  return bits.join(" · ");
}

/* ------------------------------------------------------------------ follow-ups */

export function FollowupsCard({ sc }: { sc: Scope }) {
  const q = useFollowups(sc);
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const key = ["followups", ...sc.scopeKey, sc.date];

  const run = useMutation({
    mutationFn: ({ f, kind }: { f: FollowupItem; kind: "done" | "snooze" }) => (kind === "done" ? doneFollowup(client, f.key) : snoozeFollowup(client, f.key, addDays(sc.date, 1))),
  });

  async function act(f: FollowupItem, kind: "done" | "snooze") {
    setLeaving((x) => new Set(x).add(f.key));
    try {
      await run.mutateAsync({ f, kind });
      setHidden((x) => new Set(x).add(f.key));
      qc.setQueryData(key, (old: typeof q.data) => (old ? { ...old, items: old.items.filter((i) => i.key !== f.key) } : old));
      void qc.invalidateQueries({ queryKey: ["followups"] });
    } catch {
      toast.show(t.followups.failed);
    } finally {
      setLeaving((x) => { const n = new Set(x); n.delete(f.key); return n; });
    }
  }

  const items = useMemo(
    () => dueNow(q.data?.items ?? [], sc.date).filter((f) => !hidden.has(f.key)),
    [q.data, hidden, sc.date],
  );
  const overdue = items.filter((f) => f.overdue).length;
  const omitted = q.data?.omitted ?? [];

  return (
    <Card className={s.wcol} aria-labelledby="h-fu" data-col="followups">
      <CardHeader
        titleId="h-fu"
        title={<>{t.followups.title} {overdue ? <Badge tone="crit">{t.followups.overdue(formatCount(overdue, sc.ctx.locale))}</Badge> : null}</>}
        subtitle={q.data ? t.followups.due(items.length, q.data.truncated) : undefined}
        action={<ViewAll as={Link} to="/followups" />}
      />
      {q.isPending ? <SkeletonRows /> : q.error ? (
        <div className={s.wb}><FailureBody error={q.error} title={t.followups.error} what="Follow-ups" onRetry={() => void q.refetch()} /></div>
      ) : (
        <div className={s.wb}>
          {items.length === 0 ? <EmptyState compact icon="check" title={t.followups.empty} description={t.followups.emptyBody} /> : items.map((f) => (
            <FollowupRow key={f.key} f={f} sc={sc} leaving={leaving.has(f.key)} onAct={act} />
          ))}
        </div>
      )}
      {omitted.length > 0 ? <p className={s.note}>{t.followups.partialNote(omitted.join(" and "))}</p> : null}
    </Card>
  );
}

function FollowupRow({ f, sc, leaving, onAct }: { f: FollowupItem; sc: Scope; leaving: boolean; onAct: (f: FollowupItem, k: "done" | "snooze") => void }) {
  const name = f.subject.name;
  const callable = isUnmasked(f.subject.phone);
  return (
    <div className={`${s.wi} ${s.fi} ${leaving ? s.out : ""}`} data-testid="followup-row">
      <Avatar id={f.subject.id} name={name} />
      <div style={{ minWidth: 0 }}>
        <div className={s.wm}>
          <span className={s.l1}>
            <b title={name}>{name}</b>
            <Chip kind={chipKind(f.kind)} />
            <span className={`${s.due} ${f.overdue ? s.od : ""}`} title={f.overdue ? t.followups.overdueTitle : undefined}>{dueLabel(f, sc)}</span>
          </span>
          <small className={s.detail}>{detailText(f, sc)}</small>
        </div>
        <div className={s.l3}>
          <span className={s.own}>{f.ownerName ? <><Avatar id={f.ownerStaffId ?? f.ownerName} name={f.ownerName} size="xs" />{f.ownerName}</> : null}</span>
          <div className={s.acts}>
            <button className={s.dn} aria-label={t.followups.doneLabel(name)} onClick={() => onAct(f, "done")} disabled={leaving}><Icon name="check" />{t.followups.done}</button>
            {callable ? <a href={telHref(f.subject.phone!)} aria-label={t.followups.callLabel(name)} title={t.followups.call}><Icon name="phone" /></a> : null}
            {callable ? <a href={whatsappHref(f.subject.phone!)} target="_blank" rel="noopener noreferrer" aria-label={t.followups.whatsappLabel(name)} title={t.followups.whatsapp}><Icon name="chat" /></a> : null}
            <button aria-label={t.followups.snoozeLabel(name)} title={t.followups.snooze} onClick={() => onAct(f, "snooze")} disabled={leaving}><Icon name="clock" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ appointments */

const APPT_TONE = { booked: "info", in: "ok", no_show: "crit", done: "ok", cancelled: "neutral" } as const;

export function AppointmentsCard({ sc }: { sc: Scope }) {
  const q = useAppointments(sc);
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const { locale, timeZone } = sc.ctx;

  const items = useMemo(() => [...(q.data?.items ?? [])].sort((a, b) => a.startsAt - b.startsAt), [q.data]);
  const nowMin = minutesOfDay(sc.nowMs, timeZone);
  const startMin = (a: AppointmentItem) => minutesOfDay(a.startsAt, timeZone);
  const next = items.find((a) => a.status === "booked" && startMin(a) >= nowMin);
  const nowIndex = items.findIndex((a) => startMin(a) >= nowMin);
  const done = items.filter((a) => a.status !== "booked").length;

  // Bring the "Now" line (or the next appointment) into view once the list is there.
  useEffect(() => {
    const body = bodyRef.current;
    const target = body?.querySelector<HTMLElement>("[data-now]") ?? body?.querySelector<HTMLElement>("[data-next]");
    const first = body?.querySelector<HTMLElement>("[data-appt]");
    if (body && target && first) body.scrollTop = Math.max(0, target.offsetTop - body.offsetTop - first.offsetHeight * 1.1);
  }, [items.length]);

  return (
    <Card className={s.wcol} aria-labelledby="h-ap" data-col="appointments">
      <CardHeader titleId="h-ap" title={t.appointments.title} subtitle={q.data ? t.appointments.sub(items.length, done, next ? formatTime(next.startsAt, timeZone) : null) : undefined} action={<ViewAll as={Link} to="/followups/appointments" />} />
      {q.isPending ? (
        <div className={s.wb} aria-busy="true">{[0, 1, 2, 3].map((i) => <div className={s.skRow} key={i}><Skeleton width={38} height={14} /><div><Skeleton width="55%" height={13} /><Skeleton width="80%" height={12} /></div></div>)}</div>
      ) : q.error ? (
        <div className={s.wb}><FailureBody error={q.error} title={t.appointments.error} what="Appointments" onRetry={() => void q.refetch()} /></div>
      ) : items.length === 0 ? (
        <div className={s.wb}><EmptyState compact icon="trial" title={t.appointments.empty} description={t.appointments.emptyBody} /></div>
      ) : (
        <div className={s.wb} ref={bodyRef}>
          {items.map((a, i) => {
            const isNext = a === next;
            const past = startMin(a) < nowMin;
            return (
              <div key={`${a.source}:${a.id}`} style={{ display: "contents" }}>
                {i === nowIndex ? <div className={s.nowl} aria-hidden="true" data-now><span>{t.appointments.now(formatTime(sc.nowMs, timeZone))}</span></div> : null}
                <div className={`${s.wi} ${s.ap} ${past ? s.past : ""} ${isNext ? s.next : ""}`} data-appt {...(isNext ? { "data-next": "" } : {})}>
                  <b>{formatTime(a.startsAt, timeZone)}</b>
                  <span className={`${s.node} ${isNext ? "" : a.status === "in" ? s.in : a.status === "no_show" ? s.no_show : ""}`} />
                  <button className={`${s.wm} ${s.apBtn}`} onClick={() => setOpen(a)} aria-haspopup="dialog">
                    <b title={a.person.name}>{a.person.name}</b> <span style={{ color: "var(--color-text-secondary)" }}>· {a.kind}</span>
                    {isNext ? <> <Badge tone="info">{t.appointments.next}</Badge></> : null}
                    {a.staff ? <span className={s.meta}><Avatar id={a.staff.id} name={a.staff.name} size="xs" />{a.staff.name}</span> : null}
                  </button>
                  <Badge tone={APPT_TONE[a.status]} dot>{t.appointments.status[a.status]}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <AppointmentDrawer appointment={open} onClose={() => setOpen(null)} sc={sc} locale={locale} timeZone={timeZone} />
    </Card>
  );
}

function actionsFor(a: AppointmentItem): Array<{ action: AppointmentAction; label: string; primary?: boolean }> {
  if (a.status === "done" || a.status === "cancelled" || a.status === "no_show") return [];
  switch (a.source) {
    case "pt": return [{ action: "no_show", label: t.appointments.actions.no_show }, { action: "done", label: t.appointments.actions.done, primary: true }];
    case "trial": return [{ action: "no_show", label: t.appointments.actions.no_show }, { action: "done", label: t.appointments.actions.trialDone, primary: true }];
    case "appointment": return [
      { action: "cancelled", label: t.appointments.actions.cancelled }, { action: "no_show", label: t.appointments.actions.no_show },
      ...(a.status === "booked" ? [{ action: "arrived" as const, label: t.appointments.actions.arrived }] : []),
      { action: "done", label: t.appointments.actions.done, primary: true },
    ];
  }
}

function AppointmentDrawer({ appointment, onClose, sc, locale, timeZone }: { appointment: AppointmentItem | null; onClose: () => void; sc: Scope; locale: string; timeZone: string }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<AppointmentAction | null>(null);
  const last = useRef<AppointmentItem | null>(null);
  if (appointment) last.current = appointment;
  const a = appointment ?? last.current;

  async function go(action: AppointmentAction) {
    if (!a) return;
    setBusy(action);
    try {
      await updateAppointment(client, a, action, new Date(sc.nowMs).toISOString());
      await qc.invalidateQueries({ queryKey: ["appointments"] });
      toast.show(t.appointments.drawer.updated);
      onClose();
    } catch {
      toast.show(t.appointments.drawer.failed);
    } finally {
      setBusy(null);
    }
  }

  const acts = a ? actionsFor(a) : [];
  return (
    <Drawer
      open={!!appointment}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={a ? `${a.person.name} · ${a.kind}` : ""}
      description={a ? `${formatDate(sc.date, locale, "weekday")} at ${formatTime(a.startsAt, timeZone)}` : undefined}
      footer={<>
        <Button onClick={onClose}>{t.appointments.drawer.close}</Button>
        {acts.map((x) => <Button key={x.action} variant={x.primary ? "primary" : "default"} busy={busy === x.action} busyLabel="Saving…" disabled={busy !== null} onClick={() => void go(x.action)}>{x.label}</Button>)}
      </>}
    >
      {a ? (
        <div className={s.drawerKv}>
          <div><small>{t.appointments.drawer.who}</small><b>{a.person.name}</b> <Chip kind={a.person.type === "lead" ? "enquiry" : "renewal"}>{a.person.type === "lead" ? "Lead" : "Member"}</Chip></div>
          <div><small>{t.appointments.drawer.when}</small><b>{formatTime(a.startsAt, timeZone)} – {formatTime(a.endsAt, timeZone)}</b></div>
          {a.staff ? <div><small>{t.appointments.drawer.with}</small><b>{a.staff.name}</b></div> : null}
          <div><small>{t.appointments.drawer.status}</small><Badge tone={APPT_TONE[a.status]} dot>{t.appointments.status[a.status]}</Badge></div>
        </div>
      ) : null}
    </Drawer>
  );
}

/* ------------------------------------------------------------------ tasks */

export function TasksCard({ sc }: { sc: Scope }) {
  const q = useTasks(sc);
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["tasks", ...sc.scopeKey, sc.date];
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [just, setJust] = useState<string | null>(null);

  const tasks = q.data ?? [];
  const open = tasks.filter((x) => x.doneAt === null).length;

  /** Optimistic: flip it now, undo it if the server says no. */
  async function toggle(task: TaskItem, next: boolean) {
    qc.setQueryData<TaskItem[]>(key, (old) => old?.map((x) => (x.id === task.id ? { ...x, doneAt: next ? sc.nowMs : null } : x)));
    setJust(next ? task.id : null);
    try {
      await (next ? doneTask(client, task.id) : reopenTask(client, task.id));
    } catch {
      // This row only: another tick may have landed since.
      qc.setQueryData<TaskItem[]>(key, (old) => old?.map((x) => (x.id === task.id ? { ...x, doneAt: task.doneAt } : x)));
      setJust(null);
      toast.show(t.tasks.failed);
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const text = title.trim();
    if (!text || adding) return;
    setAdding(true);
    try {
      const res = await createTask(client, { title: text.slice(0, 140), dueDate: sc.date, branchId: sc.branchId ?? sc.scopeKey[1] ?? undefined });
      qc.setQueryData<TaskItem[]>(key, (old) => [res.task, ...(old ?? [])]);
      setTitle("");
    } catch {
      toast.show(t.tasks.addFailed);
    } finally {
      setAdding(false);
    }
  }

  return (
    <Card className={s.wcol} aria-labelledby="h-tk" data-col="tasks">
      <CardHeader titleId="h-tk" title={t.tasks.title} subtitle={q.data ? t.tasks.sub(open, tasks.length - open) : undefined} action={<ViewAll as={Link} to="/followups/tasks" />} />
      {q.isPending ? <SkeletonRows /> : q.error ? (
        <div className={s.wb}><FailureBody error={q.error} title={t.tasks.error} what="Tasks" onRetry={() => void q.refetch()} /></div>
      ) : (
        <div className={`${s.wb} ${s.tk}`}>
          {tasks.length === 0 ? <EmptyState compact icon="check" title={t.tasks.empty} description={t.tasks.emptyBody} /> : tasks.map((x) => {
            const done = x.doneAt !== null;
            const when = x.dueTime ? x.dueTime : x.dueDate === null || x.dueDate === sc.date ? null : formatDate(x.dueDate, sc.ctx.locale, "weekday");
            return (
              <div key={x.id} className={`${s.wi} ${done ? s.doneRow : ""}`} data-testid="task-row">
                <Tick checked={done} onChange={(next) => void toggle(x, next)} label={t.tasks.doneLabel(x.title)} justChecked={just === x.id} />
                <div className={s.wm}>
                  <StrikeText done={done} animate={just === x.id}><span title={x.title}>{x.title}</span></StrikeText>
                  <span className={s.meta}>{x.assigneeName ? <><Avatar id={x.assigneeStaffId} name={x.assigneeName} size="xs" />{x.assigneeName} · </> : null}{done ? t.tasks.doneWord : when ? t.tasks.due(when) : t.tasks.dueToday}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <form className={s.qadd} onSubmit={add}>
        <input value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} placeholder={t.tasks.addLabel} aria-label={t.tasks.addLabel} autoComplete="off" />
        <Button size="sm" type="submit" busy={adding} busyLabel={t.tasks.adding}>{t.tasks.add}</Button>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ band */

export function WorkBand({ sc }: { sc: Scope }) {
  const [tab, setTab] = useState<"followups" | "appointments" | "tasks">("followups");
  const fu = useFollowups(sc);
  const ap = useAppointments(sc);
  const tk = useTasks(sc);
  const count = (n: number | undefined) => (n === undefined ? "" : ` ${formatCount(n, sc.ctx.locale)}`);
  return (
    <div className={s.workWrap}>
      <SegmentedTabs
        className={s.tabs}
        label={t.tabs.label}
        value={tab}
        onChange={setTab}
        options={[
          { value: "followups", label: `${t.tabs.followups}${count(fu.data && dueNow(fu.data.items, sc.date).length)}` },
          { value: "appointments", label: `${t.tabs.appointments}${count(ap.data?.items.length)}` },
          { value: "tasks", label: `${t.tabs.tasks}${count(tk.data?.filter((x) => x.doneAt === null).length)}` },
        ]}
      />
      <div className={s.work} data-tab={tab}>
        <FollowupsCard key={sc.date} sc={sc} />
        <AppointmentsCard sc={sc} />
        <TasksCard sc={sc} />
      </div>
    </div>
  );
}
